import {
  buildOntologyMatchingPlan,
  type OntologyIntentChunk,
  type OntologyMatchingAsset,
  type OntologyMatchingLlmPatch,
  type OntologyMatchingPlan,
  type OntologyMatchingSelectedAuthority,
} from "./application-ontology-matching";

export const FIGMA_TEMPLATE_ONTOLOGY_BENCHMARK_SCHEMA_VERSION =
  "cogo.figma-template-ontology-benchmark.v1";

export type FigmaTemplateBenchmarkTier =
  | "mobile-simple"
  | "mobile-commerce"
  | "mobile-dashboard"
  | "web-landing"
  | "web-dashboard"
  | "full-design-system";

export type FigmaTemplatePlatform = "mobile" | "web";

export type FigmaTemplateComponentKind =
  | "button"
  | "input"
  | "card"
  | "tabs"
  | "table"
  | "nav"
  | "modal"
  | "chart"
  | "image"
  | "text";

export const FIGMA_TEMPLATE_QUALITY_READINESS_SCHEMA_VERSION =
  "cogo.figma-template-quality-readiness.v1";

export type FigmaTemplateQualityReadinessStatus = "pass" | "fail";

export type FigmaTemplateQualityMetricName =
  | "source-geometry"
  | "composition-depth"
  | "visual-density"
  | "visual-style-coverage"
  | "interaction-evidence";

export interface FigmaTemplateLayoutBox {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}

export interface FigmaTemplateNode {
  id: string;
  name: string;
  type: string;
  characters?: string;
  componentKind?: FigmaTemplateComponentKind;
  layout?: FigmaTemplateLayoutBox;
  children?: readonly FigmaTemplateNode[];
  fills?: readonly string[];
  typography?: readonly string[];
  actions?: readonly string[];
  dataInputs?: readonly string[];
}

export interface FigmaTemplateQualityMetric {
  name: FigmaTemplateQualityMetricName;
  status: FigmaTemplateQualityReadinessStatus;
  score: number;
  threshold: number;
  evidenceRefs: string[];
  reasons: string[];
}

export interface FigmaTemplateQualityReadiness {
  schemaVersion: typeof FIGMA_TEMPLATE_QUALITY_READINESS_SCHEMA_VERSION;
  status: FigmaTemplateQualityReadinessStatus;
  score: number;
  threshold: number;
  metrics: FigmaTemplateQualityMetric[];
  evidenceLimits: string[];
}

export interface FigmaTemplateBenchmarkCase {
  id: string;
  title: string;
  tier: FigmaTemplateBenchmarkTier;
  platform: FigmaTemplatePlatform;
  intent: string;
  templateId: string;
  designSystemId: string;
  root: FigmaTemplateNode;
  requiredPageIds?: readonly string[];
  requiredUiBlockIds?: readonly string[];
  requiredComponentIds?: readonly string[];
}

export interface FigmaTemplateOntologyAssetSet {
  templateId: string;
  designSystemId: string;
  assets: OntologyMatchingAsset[];
  intentChunks: OntologyIntentChunk[];
  selected: OntologyMatchingSelectedAuthority;
  extractedTokens: string[];
  componentKinds: string[];
  evidenceRefs: string[];
}

export type FigmaTemplateBenchmarkStatus = "pass" | "fail";

export interface FigmaTemplateBenchmarkCaseResult {
  caseId: string;
  title: string;
  tier: FigmaTemplateBenchmarkTier;
  platform: FigmaTemplatePlatform;
  status: FigmaTemplateBenchmarkStatus;
  reasons: string[];
  assetSet: FigmaTemplateOntologyAssetSet;
  matchingPlan: OntologyMatchingPlan;
  qualityReadiness: FigmaTemplateQualityReadiness;
}

export interface FigmaTemplateOntologyBenchmarkReport {
  schemaVersion: typeof FIGMA_TEMPLATE_ONTOLOGY_BENCHMARK_SCHEMA_VERSION;
  status: FigmaTemplateBenchmarkStatus;
  requiredTierOrder: readonly FigmaTemplateBenchmarkTier[];
  caseCount: number;
  passedCaseCount: number;
  failedCaseCount: number;
  cases: FigmaTemplateBenchmarkCaseResult[];
}

export interface RunFigmaTemplateOntologyBenchmarkOptions {
  llmPatchByCaseId?: Readonly<Record<string, OntologyMatchingLlmPatch>>;
  requiredTierOrder?: readonly FigmaTemplateBenchmarkTier[];
}

const DEFAULT_REQUIRED_TIER_ORDER: readonly FigmaTemplateBenchmarkTier[] = [
  "mobile-simple",
  "mobile-commerce",
  "web-landing",
];

const ACTIONFLOW_VALIDATIONS = [
  "compilerParamBindings",
  "validateActionFlowTopologyContract",
  "runtime-simulation",
  "browser-trace",
] as const;

const NODE_TYPES_FOR_BLOCKS = new Set([
  "FRAME",
  "GROUP",
  "SECTION",
  "INSTANCE",
  "COMPONENT",
]);

const COMPONENT_KIND_TO_ID: Record<FigmaTemplateComponentKind, string> = {
  button: "button",
  input: "input",
  card: "card",
  tabs: "tabs",
  table: "data_table",
  nav: "navigation",
  modal: "dialog",
  chart: "chart",
  image: "image",
  text: "text",
};

export function mapFigmaTemplateComponentKindToOntologyId(
  kind: FigmaTemplateComponentKind,
): string {
  return COMPONENT_KIND_TO_ID[kind];
}

export function buildFigmaTemplateOntologyAssetSet(
  benchmarkCase: FigmaTemplateBenchmarkCase,
): FigmaTemplateOntologyAssetSet {
  const rootPageId = `${benchmarkCase.id}:page:${slugify(benchmarkCase.root.name)}`;
  const flattened = flattenNodes(benchmarkCase.root);
  const assetsById = new Map<string, OntologyMatchingAsset>();
  const topLevelBlocks = (benchmarkCase.root.children ?? []).filter((node) =>
    isBlockNode(node),
  );
  const componentKinds = unique(
    flattened
      .map((entry) => inferComponentKind(entry.node))
      .filter((kind): kind is FigmaTemplateComponentKind => Boolean(kind)),
  );
  const extractedTokens = unique(
    flattened.flatMap(({ node }) => [
      ...(node.fills ?? []),
      ...(node.typography ?? []),
    ]),
  );
  const dataInputs = unique(
    flattened.flatMap(({ node }) => node.dataInputs ?? []),
  );
  const actions = unique(flattened.flatMap(({ node }) => node.actions ?? []));
  const blockIds = topLevelBlocks.map(
    (node) => `${benchmarkCase.id}:block:${slugify(node.name)}`,
  );

  assetsById.set(rootPageId, {
    id: rootPageId,
    kind: "page",
    label: benchmarkCase.root.name,
    description: [
      benchmarkCase.title,
      benchmarkCase.intent,
      benchmarkCase.tier,
      benchmarkCase.platform,
      ...dataInputs,
      ...actions,
    ].join(" "),
    tags: [
      benchmarkCase.tier,
      benchmarkCase.platform,
      ...tokensFromText(benchmarkCase.title),
      ...tokensFromText(benchmarkCase.intent),
    ],
    ontologyRefs: [
      `figma-template:${benchmarkCase.id}:node:${benchmarkCase.root.id}`,
    ],
    chunkHandles: [`figma-template:${benchmarkCase.id}:intent`],
    templateIds: [benchmarkCase.templateId],
    designSystemIds: [benchmarkCase.designSystemId],
    requiredChildIds: blockIds,
    actionOutputs: actions,
    dataInputs,
  });

  for (const node of topLevelBlocks) {
    const nodeDescendants = flattenNodes(node).map((entry) => entry.node);
    const nodeComponents = unique(
      nodeDescendants
        .map(inferComponentKind)
        .filter((kind): kind is FigmaTemplateComponentKind => Boolean(kind))
        .map((kind) => COMPONENT_KIND_TO_ID[kind]),
    );
    const nodeActions = unique(
      nodeDescendants.flatMap((entry) => entry.actions ?? []),
    );
    const nodeDataInputs = unique(
      nodeDescendants.flatMap((entry) => entry.dataInputs ?? []),
    );
    const blockId = `${benchmarkCase.id}:block:${slugify(node.name)}`;
    assetsById.set(blockId, {
      id: blockId,
      kind: "ui-block",
      label: node.name,
      description: [
        node.name,
        node.characters,
        ...nodeDataInputs,
        ...nodeActions,
        ...nodeComponents,
      ]
        .filter(Boolean)
        .join(" "),
      tags: [
        ...tokensFromText(node.name),
        ...nodeComponents,
        ...nodeDataInputs,
        ...nodeActions,
      ],
      ontologyRefs: [`figma-template:${benchmarkCase.id}:node:${node.id}`],
      chunkHandles: [`figma-template:${benchmarkCase.id}:block:${node.id}`],
      templateIds: [benchmarkCase.templateId],
      designSystemIds: [benchmarkCase.designSystemId],
      requiredComponents: nodeComponents,
      actionOutputs: nodeActions,
      dataInputs: nodeDataInputs,
    });
  }

  for (const kind of componentKinds) {
    const componentId = COMPONENT_KIND_TO_ID[kind];
    const matchingNodes = flattened
      .filter(({ node }) => inferComponentKind(node) === kind)
      .map(({ node }) => node);
    assetsById.set(componentId, {
      id: componentId,
      kind: "component",
      label: kind,
      description: matchingNodes
        .map((node) => [node.name, node.characters].filter(Boolean).join(" "))
        .join(" "),
      tags: [
        kind,
        componentId,
        ...matchingNodes.flatMap((node) => tokensFromText(node.name)),
      ],
      ontologyRefs: matchingNodes.map(
        (node) => `figma-template:${benchmarkCase.id}:node:${node.id}`,
      ),
      chunkHandles: [`figma-template:${benchmarkCase.id}:component:${kind}`],
      templateIds: [benchmarkCase.templateId],
      designSystemIds: [benchmarkCase.designSystemId],
    });
  }

  const requiredPageIds = benchmarkCase.requiredPageIds ?? [rootPageId];
  const requiredUiBlockIds = benchmarkCase.requiredUiBlockIds ?? blockIds;
  const requiredComponentIds =
    benchmarkCase.requiredComponentIds ??
    componentKinds.map((kind) => COMPONENT_KIND_TO_ID[kind]);

  return {
    templateId: benchmarkCase.templateId,
    designSystemId: benchmarkCase.designSystemId,
    assets: [...assetsById.values()],
    intentChunks: [
      {
        id: `figma-template:${benchmarkCase.id}:intent`,
        text: benchmarkCase.intent,
        weight: 2,
        tags: [benchmarkCase.tier, benchmarkCase.platform],
      },
      ...topLevelBlocks.map((node) => ({
        id: `figma-template:${benchmarkCase.id}:block:${node.id}`,
        text: [node.name, node.characters].filter(Boolean).join(" "),
        weight: 1,
        tags: ["figma-block", benchmarkCase.tier],
      })),
    ],
    selected: {
      designSystemId: benchmarkCase.designSystemId,
      templateId: benchmarkCase.templateId,
      requiredPageIds,
      requiredUiBlockIds,
      requiredComponentIds,
    },
    extractedTokens,
    componentKinds: componentKinds.map((kind) => COMPONENT_KIND_TO_ID[kind]),
    evidenceRefs: flattened.map(
      ({ node }) => `figma-template:${benchmarkCase.id}:node:${node.id}`,
    ),
  };
}

export function runFigmaTemplateOntologyBenchmark(
  benchmarkCases = buildDefaultFigmaTemplateBenchmarkCases(),
  options: RunFigmaTemplateOntologyBenchmarkOptions = {},
): FigmaTemplateOntologyBenchmarkReport {
  const requiredTierOrder =
    options.requiredTierOrder ?? DEFAULT_REQUIRED_TIER_ORDER;
  const cases = benchmarkCases.map((benchmarkCase) => {
    const assetSet = buildFigmaTemplateOntologyAssetSet(benchmarkCase);
    const matchingPlan = buildOntologyMatchingPlan({
      intent: benchmarkCase.intent,
      chunks: assetSet.intentChunks,
      assets: assetSet.assets,
      selected: assetSet.selected,
      llmPatch: options.llmPatchByCaseId?.[benchmarkCase.id],
    });
    const qualityReadiness = buildFigmaTemplateQualityReadiness(benchmarkCase);
    const reasons = evaluateCaseReadiness(
      benchmarkCase,
      assetSet,
      matchingPlan,
      qualityReadiness,
    );
    return {
      caseId: benchmarkCase.id,
      title: benchmarkCase.title,
      tier: benchmarkCase.tier,
      platform: benchmarkCase.platform,
      status: reasons.length === 0 ? "pass" : "fail",
      reasons,
      assetSet,
      matchingPlan,
      qualityReadiness,
    } satisfies FigmaTemplateBenchmarkCaseResult;
  });
  const tierReasons = evaluateTierOrder(cases, requiredTierOrder);
  const casesWithTierGate =
    tierReasons.length === 0
      ? cases
      : cases.map((entry) =>
          requiredTierOrder.includes(entry.tier)
            ? {
                ...entry,
                status: "fail" as const,
                reasons: [...entry.reasons, ...tierReasons],
              }
            : entry,
        );
  const failedCaseCount = casesWithTierGate.filter(
    (entry) => entry.status === "fail",
  ).length;

  return {
    schemaVersion: FIGMA_TEMPLATE_ONTOLOGY_BENCHMARK_SCHEMA_VERSION,
    status: failedCaseCount === 0 ? "pass" : "fail",
    requiredTierOrder,
    caseCount: casesWithTierGate.length,
    passedCaseCount: casesWithTierGate.length - failedCaseCount,
    failedCaseCount,
    cases: casesWithTierGate,
  };
}

export function buildDefaultFigmaTemplateBenchmarkCases(): FigmaTemplateBenchmarkCase[] {
  return [
    {
      id: "mobile-onboarding-auth",
      title: "Mobile onboarding auth template",
      tier: "mobile-simple",
      platform: "mobile",
      intent:
        "Create a mobile onboarding and sign in app with email input, password input, and continue action.",
      templateId: "figma-community-mobile-onboarding-v0",
      designSystemId: "figma-community-clean-mobile-v0",
      root: {
        id: "1:1",
        name: "Mobile Auth Flow",
        type: "FRAME",
        layout: { x: 0, y: 0, width: 390, height: 844 },
        fills: ["#ffffff", "#111827"],
        typography: ["Inter/Title", "Inter/Body"],
        dataInputs: ["email", "password"],
        actions: ["continueWithEmail"],
        children: [
          {
            id: "1:2",
            name: "Welcome Card",
            type: "FRAME",
            layout: { x: 24, y: 96, width: 342, height: 420 },
            fills: ["#f8fafc"],
            componentKind: "card",
            children: [
              {
                id: "1:3",
                name: "Headline",
                type: "TEXT",
                componentKind: "text",
                layout: { x: 48, y: 132, width: 282, height: 34 },
                typography: ["Inter/Title"],
                characters: "Start your account",
              },
              {
                id: "1:7",
                name: "Supporting Copy",
                type: "TEXT",
                componentKind: "text",
                layout: { x: 48, y: 178, width: 282, height: 48 },
                typography: ["Inter/Body"],
                characters:
                  "Use your email to continue with a secure workspace.",
              },
              {
                id: "1:4",
                name: "Email Input",
                type: "INSTANCE",
                componentKind: "input",
                layout: { x: 48, y: 250, width: 282, height: 48 },
                fills: ["#ffffff"],
                dataInputs: ["email"],
              },
              {
                id: "1:5",
                name: "Password Input",
                type: "INSTANCE",
                componentKind: "input",
                layout: { x: 48, y: 314, width: 282, height: 48 },
                fills: ["#ffffff"],
                dataInputs: ["password"],
              },
              {
                id: "1:6",
                name: "Continue Button",
                type: "INSTANCE",
                componentKind: "button",
                layout: { x: 48, y: 386, width: 282, height: 52 },
                fills: ["#2563eb"],
                actions: ["continueWithEmail"],
              },
            ],
          },
        ],
      },
    },
    {
      id: "mobile-booking-list-detail",
      title: "Mobile booking list and detail template",
      tier: "mobile-commerce",
      platform: "mobile",
      intent:
        "Create a mobile booking app with service cards, date selection, checkout summary, and reservation action.",
      templateId: "figma-community-mobile-booking-v0",
      designSystemId: "figma-community-market-mobile-v0",
      root: {
        id: "2:1",
        name: "Booking Flow",
        type: "FRAME",
        layout: { x: 0, y: 0, width: 390, height: 844 },
        fills: ["#f8fafc", "#0f766e"],
        typography: ["Inter/Title", "Inter/Body"],
        dataInputs: ["service", "date", "time"],
        actions: ["selectService", "reserveSlot"],
        children: [
          {
            id: "2:2",
            name: "Service List",
            type: "FRAME",
            componentKind: "card",
            layout: { x: 20, y: 96, width: 350, height: 308 },
            fills: ["#ffffff"],
            dataInputs: ["service"],
            actions: ["selectService"],
            children: [
              {
                id: "2:8",
                name: "Service Heading",
                type: "TEXT",
                componentKind: "text",
                layout: { x: 44, y: 124, width: 302, height: 30 },
                typography: ["Inter/Title"],
                characters: "Choose a service",
              },
              {
                id: "2:3",
                name: "Service Card",
                type: "INSTANCE",
                componentKind: "card",
                layout: { x: 44, y: 176, width: 302, height: 112 },
                fills: ["#f0fdfa"],
              },
              {
                id: "2:4",
                name: "Book Button",
                type: "INSTANCE",
                componentKind: "button",
                layout: { x: 44, y: 316, width: 302, height: 52 },
                fills: ["#0f766e"],
                actions: ["selectService"],
              },
            ],
          },
          {
            id: "2:5",
            name: "Checkout Summary",
            type: "FRAME",
            layout: { x: 20, y: 428, width: 350, height: 260 },
            fills: ["#ffffff"],
            dataInputs: ["date", "time"],
            actions: ["reserveSlot"],
            children: [
              {
                id: "2:9",
                name: "Checkout Heading",
                type: "TEXT",
                componentKind: "text",
                layout: { x: 44, y: 460, width: 302, height: 30 },
                typography: ["Inter/Title"],
                characters: "Confirm your slot",
              },
              {
                id: "2:6",
                name: "Date Input",
                type: "INSTANCE",
                componentKind: "input",
                layout: { x: 44, y: 512, width: 302, height: 48 },
                fills: ["#ffffff"],
                dataInputs: ["date"],
              },
              {
                id: "2:7",
                name: "Reserve Button",
                type: "INSTANCE",
                componentKind: "button",
                layout: { x: 44, y: 592, width: 302, height: 52 },
                fills: ["#0f766e"],
                actions: ["reserveSlot"],
              },
            ],
          },
        ],
      },
    },
    {
      id: "web-saas-landing",
      title: "Web SaaS landing template",
      tier: "web-landing",
      platform: "web",
      intent:
        "Create a web SaaS landing page with navigation, hero proof, feature cards, and call to action.",
      templateId: "figma-community-web-saas-landing-v0",
      designSystemId: "figma-community-saas-web-v0",
      root: {
        id: "3:1",
        name: "SaaS Landing Page",
        type: "FRAME",
        layout: { x: 0, y: 0, width: 1440, height: 900 },
        fills: ["#ffffff", "#2563eb"],
        typography: ["Inter/Display", "Inter/Body"],
        actions: ["openSignup", "openDemo"],
        children: [
          {
            id: "3:2",
            name: "Top Navigation",
            type: "FRAME",
            componentKind: "nav",
            layout: { x: 80, y: 32, width: 1280, height: 64 },
            actions: ["openSignup"],
            children: [
              {
                id: "3:9",
                name: "Brand Label",
                type: "TEXT",
                componentKind: "text",
                layout: { x: 96, y: 52, width: 160, height: 24 },
                typography: ["Inter/Body"],
                characters: "Cogo Studio",
              },
              {
                id: "3:3",
                name: "Signup Button",
                type: "INSTANCE",
                componentKind: "button",
                layout: { x: 1208, y: 44, width: 136, height: 44 },
                fills: ["#2563eb"],
                actions: ["openSignup"],
              },
            ],
          },
          {
            id: "3:4",
            name: "Hero Section",
            type: "FRAME",
            layout: { x: 80, y: 160, width: 1280, height: 388 },
            actions: ["openDemo"],
            children: [
              {
                id: "3:5",
                name: "Hero Headline",
                type: "TEXT",
                componentKind: "text",
                layout: { x: 120, y: 196, width: 720, height: 68 },
                typography: ["Inter/Display"],
                characters: "Launch faster with Cogo",
              },
              {
                id: "3:10",
                name: "Hero Supporting Copy",
                type: "TEXT",
                componentKind: "text",
                layout: { x: 120, y: 292, width: 620, height: 64 },
                typography: ["Inter/Body"],
                characters:
                  "Turn a sourced design template into a governed app with ontology-backed UI and ActionFlow evidence.",
              },
              {
                id: "3:6",
                name: "Demo Button",
                type: "INSTANCE",
                componentKind: "button",
                layout: { x: 120, y: 392, width: 156, height: 48 },
                fills: ["#2563eb"],
                actions: ["openDemo"],
              },
            ],
          },
          {
            id: "3:7",
            name: "Feature Cards",
            type: "FRAME",
            layout: { x: 80, y: 596, width: 1280, height: 188 },
            children: [
              {
                id: "3:8",
                name: "Feature Card",
                type: "INSTANCE",
                componentKind: "card",
                layout: { x: 120, y: 620, width: 360, height: 132 },
                fills: ["#f8fafc"],
              },
            ],
          },
        ],
      },
    },
  ];
}

function evaluateCaseReadiness(
  benchmarkCase: FigmaTemplateBenchmarkCase,
  assetSet: FigmaTemplateOntologyAssetSet,
  matchingPlan: OntologyMatchingPlan,
  qualityReadiness = buildFigmaTemplateQualityReadiness(benchmarkCase),
): string[] {
  const reasons: string[] = [];
  const hardConstraints = new Set(matchingPlan.hardConstraints);
  if (
    !hardConstraints.has(`design-system:${benchmarkCase.designSystemId}:hard`)
  ) {
    reasons.push(
      "Selected design system was not preserved as a hard constraint.",
    );
  }
  if (!hardConstraints.has(`template:${benchmarkCase.templateId}:hard`)) {
    reasons.push("Selected template was not preserved as a hard constraint.");
  }
  for (const id of assetSet.selected.requiredPageIds ?? []) {
    if (!hardConstraints.has(`required-page:${id}`)) {
      reasons.push(`Required page ${id} was not enforced.`);
    }
  }
  for (const decision of matchingPlan.decisions) {
    if (decision.candidateLifecycleStatus === "candidate") {
      reasons.push(
        `Candidate-only ${decision.kind} ${decision.requirementId} blocks template readiness.`,
      );
    }
    if (decision.authority !== "tool") {
      reasons.push(
        `Decision ${decision.requirementId} used non-tool authority ${decision.authority}.`,
      );
    }
  }
  if (matchingPlan.actionFlowImpact.required) {
    const validations = new Set(
      matchingPlan.actionFlowImpact.requiredValidation,
    );
    for (const validation of ACTIONFLOW_VALIDATIONS) {
      if (!validations.has(validation)) {
        reasons.push(
          `ActionFlow impact is missing required validation ${validation}.`,
        );
      }
    }
  }
  if (assetSet.extractedTokens.length === 0) {
    reasons.push(
      "No design tokens were extracted from the Figma template tree.",
    );
  }
  if (assetSet.componentKinds.length === 0) {
    reasons.push("No component ontology candidates were extracted.");
  }
  if (qualityReadiness.status !== "pass") {
    reasons.push(
      `Figma template quality readiness failed with score ${qualityReadiness.score}.`,
    );
    for (const metric of qualityReadiness.metrics) {
      if (metric.status === "fail") {
        reasons.push(...metric.reasons);
      }
    }
  }
  return reasons;
}

export function buildFigmaTemplateQualityReadiness(
  benchmarkCase: FigmaTemplateBenchmarkCase,
): FigmaTemplateQualityReadiness {
  const flattened = flattenNodes(benchmarkCase.root);
  const renderable = flattened
    .map((entry) => entry.node)
    .filter(isRenderableQualityNode);
  const componentKinds = unique(
    renderable
      .map((node) => node.componentKind)
      .filter((kind): kind is FigmaTemplateComponentKind => Boolean(kind)),
  );
  const textNodes = renderable.filter((node) => node.type === "TEXT");
  const styleBearing = renderable.filter(hasQualityStyleSource);
  const geometryBearing = renderable.filter(hasUsableLayout);
  const contentNodes = renderable.filter(
    (node) => node.id !== benchmarkCase.root.id,
  );
  const styledContentNodes = contentNodes.filter(hasQualityStyleSource);
  const nestedContentContainers = contentNodes.filter(
    (node) => (node.children ?? []).filter(isRenderableQualityNode).length >= 2,
  );
  const metrics: FigmaTemplateQualityMetric[] = [
    qualityMetric({
      name: "source-geometry",
      score:
        renderable.length > 0
          ? roundScore(geometryBearing.length / renderable.length)
          : 0,
      threshold: 0.85,
      evidenceRefs: geometryBearing.map((node) =>
        figmaNodeRef(benchmarkCase.id, node.id),
      ),
      reasons: [
        ...(hasUsableLayout(benchmarkCase.root)
          ? []
          : ["Figma template root is missing width/height geometry."]),
        ...(geometryBearing.length === renderable.length
          ? []
          : [
              `Only ${geometryBearing.length}/${renderable.length} renderable Figma nodes expose geometry for layout fidelity.`,
            ]),
      ],
    }),
    qualityMetric({
      name: "composition-depth",
      score: roundScore(
        [
          renderable.length >= 7,
          (benchmarkCase.root.children?.length ?? 0) >= 1,
          textNodes.length >= 2,
          componentKinds.length >= 2,
        ].filter(Boolean).length / 4,
      ),
      threshold: 0.75,
      evidenceRefs: renderable.map((node) =>
        figmaNodeRef(benchmarkCase.id, node.id),
      ),
      reasons: [
        ...(renderable.length >= 7
          ? []
          : ["Figma template has too few renderable nodes for app quality."]),
        ...(textNodes.length >= 2
          ? []
          : [
              "Figma template needs at least two text nodes for readable hierarchy.",
            ]),
        ...(componentKinds.length >= 2
          ? []
          : [
              "Figma template needs at least two component kinds before app generation.",
            ]),
      ],
    }),
    qualityMetric({
      name: "visual-density",
      score: roundScore(
        [
          contentNodes.length >= 6,
          styledContentNodes.length >= 4,
          nestedContentContainers.length >= 1,
          textNodes.length >= 2,
          componentKinds.length >= 3,
        ].filter(Boolean).length / 5,
      ),
      threshold: 0.8,
      evidenceRefs: contentNodes.map((node) =>
        figmaNodeRef(benchmarkCase.id, node.id),
      ),
      reasons: [
        ...(contentNodes.length >= 6
          ? []
          : [
              `Only ${contentNodes.length} content node(s) were found; Figma template validation needs at least 6 non-root renderable nodes.`,
            ]),
        ...(styledContentNodes.length >= 4
          ? []
          : [
              `Only ${styledContentNodes.length} content node(s) expose visual style evidence; design validation needs at least 4.`,
            ]),
        ...(nestedContentContainers.length >= 1
          ? []
          : [
              "Figma template needs at least one nested content group/card/section with multiple child nodes.",
            ]),
        ...(componentKinds.length >= 3
          ? []
          : [
              "Figma template needs at least three component kinds for design-quality validation.",
            ]),
      ],
    }),
    qualityMetric({
      name: "visual-style-coverage",
      score:
        renderable.length > 0
          ? roundScore(styleBearing.length / renderable.length)
          : 0,
      threshold: 0.6,
      evidenceRefs: styleBearing.map((node) =>
        figmaNodeRef(benchmarkCase.id, node.id),
      ),
      reasons: [
        ...(styleBearing.length / Math.max(renderable.length, 1) >= 0.6
          ? []
          : [
              `Only ${styleBearing.length}/${renderable.length} renderable Figma nodes expose token/style evidence.`,
            ]),
      ],
    }),
    qualityMetric({
      name: "interaction-evidence",
      score: roundScore(
        [
          flattened.some(({ node }) => (node.actions ?? []).length > 0),
          componentKinds.includes("button"),
          benchmarkCase.platform === "web" ||
            componentKinds.includes("input") ||
            componentKinds.includes("nav"),
        ].filter(Boolean).length / 3,
      ),
      threshold: 0.67,
      evidenceRefs: flattened
        .filter(({ node }) => (node.actions ?? []).length > 0)
        .map(({ node }) => figmaNodeRef(benchmarkCase.id, node.id)),
      reasons: [
        ...(flattened.some(({ node }) => (node.actions ?? []).length > 0)
          ? []
          : ["Figma template has no action-bearing nodes to validate."]),
        ...(componentKinds.includes("button")
          ? []
          : ["Figma template has no button component for task completion."]),
      ],
    }),
  ];
  const score = roundScore(
    metrics.reduce((sum, metric) => sum + metric.score, 0) / metrics.length,
  );
  const threshold = 0.82;
  const failed = metrics.some((metric) => metric.status === "fail");
  return {
    schemaVersion: FIGMA_TEMPLATE_QUALITY_READINESS_SCHEMA_VERSION,
    status: !failed && score >= threshold ? "pass" : "fail",
    score,
    threshold,
    metrics,
    evidenceLimits: [
      "Headless readiness verifies source structure, geometry, style evidence, and interaction hooks; screenshot parity still requires browser visual diff against the source frame.",
    ],
  };
}

function qualityMetric(input: {
  name: FigmaTemplateQualityMetricName;
  score: number;
  threshold: number;
  evidenceRefs: string[];
  reasons: string[];
}): FigmaTemplateQualityMetric {
  return {
    ...input,
    status: input.score >= input.threshold ? "pass" : "fail",
    reasons:
      input.score >= input.threshold
        ? []
        : input.reasons.length > 0
          ? input.reasons
          : [
              `Figma template quality metric ${input.name} scored ${input.score}; required ${input.threshold}.`,
            ],
  };
}

function isRenderableQualityNode(node: FigmaTemplateNode): boolean {
  return !["DOCUMENT", "CANVAS"].includes(node.type);
}

function hasUsableLayout(node: FigmaTemplateNode): boolean {
  return (
    typeof node.layout?.width === "number" &&
    node.layout.width > 0 &&
    typeof node.layout?.height === "number" &&
    node.layout.height > 0
  );
}

function hasQualityStyleSource(node: FigmaTemplateNode): boolean {
  return Boolean(
    (node.fills?.length ?? 0) > 0 ||
    (node.typography?.length ?? 0) > 0 ||
    node.componentKind === "input" ||
    node.componentKind === "button",
  );
}

function roundScore(value: number): number {
  return Number(value.toFixed(3));
}

function figmaNodeRef(caseId: string, nodeId: string): string {
  return `figma-template:${caseId}:node:${nodeId}`;
}

function evaluateTierOrder(
  cases: readonly FigmaTemplateBenchmarkCaseResult[],
  requiredTierOrder: readonly FigmaTemplateBenchmarkTier[],
): string[] {
  const tierIndex = new Map<FigmaTemplateBenchmarkTier, number>();
  cases.forEach((entry, index) => {
    if (!tierIndex.has(entry.tier)) tierIndex.set(entry.tier, index);
  });
  const reasons: string[] = [];
  for (const tier of requiredTierOrder) {
    if (!tierIndex.has(tier)) {
      reasons.push(`Required benchmark tier ${tier} is missing.`);
    }
  }
  const indexes = requiredTierOrder
    .map((tier) => tierIndex.get(tier))
    .filter((index): index is number => index !== undefined);
  for (let i = 1; i < indexes.length; i += 1) {
    if (indexes[i] < indexes[i - 1]) {
      reasons.push(
        `Benchmark tier order must start ${requiredTierOrder.join(" -> ")}.`,
      );
      break;
    }
  }
  return reasons;
}

function inferComponentKind(
  node: FigmaTemplateNode,
): FigmaTemplateComponentKind | null {
  if (node.componentKind) return node.componentKind;
  const normalized = slugify(`${node.type} ${node.name}`);
  if (normalized.includes("button")) return "button";
  if (normalized.includes("input") || normalized.includes("field"))
    return "input";
  if (normalized.includes("card")) return "card";
  if (normalized.includes("table") || normalized.includes("list"))
    return "table";
  if (normalized.includes("nav")) return "nav";
  if (normalized.includes("modal") || normalized.includes("dialog"))
    return "modal";
  if (normalized.includes("chart")) return "chart";
  if (node.type === "TEXT") return "text";
  if (node.type === "IMAGE" || normalized.includes("image")) return "image";
  return null;
}

function isBlockNode(node: FigmaTemplateNode): boolean {
  return NODE_TYPES_FOR_BLOCKS.has(node.type) || Boolean(node.children?.length);
}

function flattenNodes(
  node: FigmaTemplateNode,
  depth = 0,
): Array<{ node: FigmaTemplateNode; depth: number }> {
  return [
    { node, depth },
    ...(node.children ?? []).flatMap((child) => flattenNodes(child, depth + 1)),
  ];
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .trim();
}

function tokensFromText(value: string | undefined): string[] {
  if (!value) return [];
  return value
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .map((token) => token.trim())
    .filter(Boolean);
}

function unique<T>(items: readonly T[]): T[] {
  return [...new Set(items)];
}
